import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/state/AuthContext';
import { ThemeProvider } from '@/state/ThemeContext';
import { AuthGuard } from '@/components/AuthGuard';
import { LandingPage } from '@/pages/Landing';
import { AuthPage } from '@/pages/Auth';
import { MarkdownPage } from '@/pages/MarkdownPage';
import { NotFoundPage } from '@/pages/NotFound';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Overview } from '@/pages/dashboard/Overview';
import { InvoiceListPage } from '@/pages/dashboard/InvoiceList';
import { UploadPage } from '@/pages/dashboard/Upload';
import { CreatePage } from '@/pages/dashboard/Create';
import { ClientsPage } from '@/pages/dashboard/Clients';
import { InvoiceDetailPage } from '@/pages/dashboard/InvoiceDetail';
import { SettingsPage } from '@/pages/dashboard/Settings';
import { ToastStack } from '@/components/ui';
import termsMd from '@/content/terms.md?raw';
import privacyMd from '@/content/privacy.md?raw';

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={qc}>
      <AuthProvider>
        <ThemeProvider>
          <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/terms" element={<MarkdownPage source={termsMd} />} />
            <Route path="/privacy" element={<MarkdownPage source={privacyMd} />} />
            <Route path="/dashboard" element={<AuthGuard><DashboardLayout /></AuthGuard>}>
              <Route index element={<Overview />} />
              <Route
                path="invoices"
                element={
                  <InvoiceListPage
                    archived="false"
                    title="Factures"
                    emptyHint={{
                      title: 'Aucune facture pour le moment',
                      lede: 'Créez votre première facture dans l\'onglet Créer, ou déplacez un document scanné depuis les Archives.',
                      ctaTo: '/dashboard/create',
                      ctaLabel: 'Nouvelle facture',
                    }}
                  />
                }
              />
              <Route path="upload" element={<UploadPage />} />
              <Route path="create" element={<CreatePage />} />
              <Route
                path="archive"
                element={
                  <InvoiceListPage
                    archived="true"
                    title="Archives"
                    emptyHint={{
                      title: 'Les archives sont vides',
                      lede: 'Les documents téléversés apparaissent ici. Déplacez ceux que vous ne souhaitez plus voir dans Factures grâce au menu de ligne.',
                      ctaTo: '/dashboard/upload',
                      ctaLabel: 'Téléverser',
                    }}
                  />
                }
              />
              <Route path="clients" element={<ClientsPage />} />
              <Route path="invoices/:id" element={<InvoiceDetailPage />} />
              <Route path="settings" element={<SettingsPage />} />
            </Route>
            <Route path="/login" element={<Navigate to="/auth" replace />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <ToastStack />
        </BrowserRouter>
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
